/**
 * "MAPPING SOURCES" — the visible provenance note in the Control Library.
 *
 * WHY THIS IS A COMPONENT ON THE SCREEN AND NOT A COMMENT IN A FILE
 * CC BY 4.0 requires credit and a statement of changes, and an attribution
 * nobody can see is not credit. It is also a fair thing to be proud of: our
 * mappings are compared with the EU agency's own.
 *
 * WHAT IT WILL NOT CLAIM
 * It does not say "checked against ENISA". It says what is true — what we
 * compare against, when, and how many comparisons a person has actually
 * reviewed — and shows how many are still waiting. The claim is only as strong
 * as the review queue behind it, and the note is honest about that queue.
 *
 * Readable by anyone who can read the catalogue (`framework.read`); the link to
 * the queue appears only for those who may open it (`org.update`).
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useContentWatchT } from '../lib/contentWatchStrings';
import { Card } from './ui';

interface Attribution {
  sources: Array<{
    key: string; name: string; url: string; licence: string; licenceUrl: string;
    attribution: string; lastCheckedAt: string | null; lastStatus: string | null;
  }>;
  reviewed: number;
  open: number;
  differences: number;
}

export function MappingSources() {
  const { tx, txn, formatDate } = useContentWatchT();
  const { allows } = useAuth();
  const { data } = useQuery({
    queryKey: ['content-watch', 'attribution'],
    queryFn: () => api.get<Attribution>('/content-watch/attribution'),
    // A provenance note must never take the page down with it.
    retry: false,
  });

  if (!data || data.sources.length === 0) return null;

  // One credit line per distinct text: the two ENISA files share it.
  const credits = [...new Map(data.sources.map((s) => [s.attribution, s])).values()];

  return (
    <Card className="p-4 sm:p-5" >
      <section aria-labelledby="mapping-sources-h">
        <h2 id="mapping-sources-h" className="text-sm font-semibold text-ink">{tx('mappingSources.title')}</h2>
        <p className="mt-1.5 text-sm text-ink-secondary max-w-4xl">{tx('mappingSources.body')}</p>

        <ul className="mt-3 space-y-2">
          {credits.map((c) => (
            <li key={c.key} className="text-xs text-ink-secondary">
              {c.attribution}{' '}
              <a href={c.licenceUrl} target="_blank" rel="noreferrer noopener" className="text-brand hover:underline">
                {tx('mappingSources.licence', { licence: c.licence })}
              </a>
            </li>
          ))}
        </ul>

        <p className="mt-3 text-xs text-ink-secondary max-w-4xl">{tx('mappingSources.changes')}</p>

        <ul className="mt-3 space-y-1 text-xs text-ink-muted">
          {data.sources.map((s) => (
            <li key={s.key}>
              <span className="text-ink-secondary">{s.name}</span>
              <span aria-hidden> · </span>
              {s.lastCheckedAt
                ? tx('mappingSources.lastChecked', { date: formatDate(s.lastCheckedAt) })
                : tx('mappingSources.never')}
            </li>
          ))}
        </ul>

        <p className="mt-3 text-xs text-ink-secondary">
          {data.reviewed === 0 && data.open === 0
            ? tx('mappingSources.status.none')
            : [
                txn('mappingSources.status.reviewed', data.reviewed),
                ...(data.open > 0 ? [txn('mappingSources.status.open', data.open)] : []),
                ...(data.differences > 0 ? [txn('mappingSources.status.differences', data.differences)] : []),
              ].join(' · ')}
          {allows('org.update') && (
            <>
              {' '}
              <Link to="/content-watch" className="text-brand hover:underline">{tx('mappingSources.open')}</Link>
            </>
          )}
        </p>
      </section>
    </Card>
  );
}
